import { db, auth } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp, collection } from "firebase/firestore";
import { importKey, exportKey, decryptMessage, encryptMessage } from "./crypto";

// 🔒 Sender Key Manager (Web)
// Handles generation, storage, and retrieval of Group Encryption Keys (AES-GCM 256)

const KEY_COLLECTION = "group_keys";

// In-memory cache to reduce reads
const keyCache = new Map(); // Map<keyId, CryptoKey>

export const SenderKeyManager = {

    // 1. Get Key (Cache -> LocalStorage -> Firestore)
    getSenderKey: async (groupId, keyId) => {
        if (!groupId || !keyId) return null;

        // A. Check Memory Cache
        if (keyCache.has(keyId)) return keyCache.get(keyId);

        // B. Check LocalStorage
        const localKeyJson = localStorage.getItem(`group_key_${keyId}`);
        if (localKeyJson) {
            try {
                const key = await importKey(localKeyJson, "decrypt-aes"); // Custom type for AES import
                if (key) {
                    keyCache.set(keyId, key);
                    return key;
                }
            } catch (e) { console.warn("Invalid local group key", e); }
        }

        // C. Fetch from Network
        return await fetchAndSaveNetworkKey(groupId, keyId);
    },

    // 2. Rotate Key (Generate New -> Encrypt for Members -> Upload)
    rotateMySenderKey: async (groupId, memberIds) => {
        const currentUser = auth.currentUser;
        if (!currentUser) return null;

        try {
            // A. Generate AES-GCM Key
            const newKey = await window.crypto.subtle.generateKey(
                { name: "AES-GCM", length: 256 },
                true,
                ["encrypt", "decrypt"]
            );

            // Export Raw Bytes for encryption
            const rawKeyBuffer = await window.crypto.subtle.exportKey("raw", newKey);
            const rawKeyBytes = new Uint8Array(rawKeyBuffer); // To base64? No, encryptMessage wants String or Buffer? 
            // crypto.js encryptHybrid/encryptMessage expects String usually, but we can pass Buffer if we adjust.
            // Actually crypto.js encryptMessage takes (message: string, publicKey).
            // We need to convert raw bytes to string (Latin1) to preserve byte values for encryption?
            // Or better: Use exportKey("jwk") and encrypt the JWK string. This is safer and standard (Web Crypto).

            const jwk = await window.crypto.subtle.exportKey("jwk", newKey);
            const jwkString = JSON.stringify(jwk);

            const keyId = crypto.randomUUID();
            const encryptedKeys = {};

            // B. Encrypt for Each Member
            // Parallelize for performance
            await Promise.all(memberIds.map(async (uid) => {
                try {
                    // Fetch User Public Key
                    // Optimization: Cache user keys elsewhere, but for now fetch.
                    // Note: In a real app, pass user objects to avoid N reads.
                    // We'll assume memberIds is just IDs.
                    let pubKeyStr = null;

                    // Try Local Cache of Public Keys (if exists in app)
                    const cachedPub = localStorage.getItem(`pubKey_${uid}`);
                    if (cachedPub) pubKeyStr = cachedPub;
                    else {
                        const userDoc = await getDoc(doc(db, "users", uid));
                        if (userDoc.exists()) pubKeyStr = userDoc.data().publicKey;
                    }

                    if (pubKeyStr) {
                        const pubKey = await importKey(pubKeyStr, "encrypt");
                        if (pubKey) {
                            // Encrypt the JWK String
                            // Use simple RSA encryption (SenderKey distribution is usually RSA-OAEP wrapped)
                            // crypto.js encryptMessage returns Base64
                            const cipher = await encryptMessage(jwkString, pubKey);
                            encryptedKeys[uid] = {
                                k: cipher,
                                iv: "" // RSA doesn't use IV
                            };
                        }
                    }
                } catch (e) {
                    console.error(`Failed to encrypt for ${uid}`, e);
                }
            }));

            // C. Upload
            const keyDoc = {
                senderId: currentUser.uid,
                createdAt: serverTimestamp(),
                encryptedKeys: encryptedKeys
            };

            await setDoc(doc(db, "groups", groupId, "keys", keyId), keyDoc);

            // D. Save Locally & Cache
            localStorage.setItem(`group_key_${keyId}`, jwkString);
            keyCache.set(keyId, newKey);

            return keyId;

        } catch (e) {
            console.error("Key Rotation Failed", e);
            return null;
        }
    },

    // 3. Crypto Helpers (AES-GCM)
    encryptText: async (text, key) => {
        try {
            const enc = new TextEncoder();
            const encoded = enc.encode(text);
            const iv = window.crypto.getRandomValues(new Uint8Array(12));

            const ciphertextBuffer = await window.crypto.subtle.encrypt(
                { name: "AES-GCM", iv: iv },
                key,
                encoded
            );

            return {
                ciphertext: btoa(String.fromCharCode(...new Uint8Array(ciphertextBuffer))),
                iv: btoa(String.fromCharCode(...iv))
            };
        } catch (e) { console.error("Text Enc Failed", e); return null; }
    },

    decryptText: async (ciphertextBase64, key, ivBase64) => {
        try {
            const iv = Uint8Array.from(atob(ivBase64), c => c.charCodeAt(0));
            const data = Uint8Array.from(atob(ciphertextBase64), c => c.charCodeAt(0));

            const decryptedBuffer = await window.crypto.subtle.decrypt(
                { name: "AES-GCM", iv: iv },
                key,
                data
            );

            return new TextDecoder().decode(decryptedBuffer);
        } catch (e) {
            console.error("Text Dec Failed", e);
            return "⚠️ Decryption Error";
        }
    },

    // Helpers
    clearCache: () => keyCache.clear()
};

// Internal Helper
async function fetchAndSaveNetworkKey(groupId, keyId) {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;

    // Get My Private Key
    const myPrivKeyStr = localStorage.getItem(`privKey_${currentUser.uid}`);
    if (!myPrivKeyStr) return null;

    try {
        const myPrivKey = await importKey(myPrivKeyStr, "decrypt");

        const docSnap = await getDoc(doc(db, "groups", groupId, "keys", keyId));
        if (!docSnap.exists()) return null;

        const data = docSnap.data();
        const myEncryptedData = data.encryptedKeys?.[currentUser.uid];

        if (myEncryptedData) {
            // Decrypt
            // myEncryptedData.k is Base64 RSA Ciphertext
            const jwkStr = await decryptMessage(myEncryptedData.k, myPrivKey);

            if (jwkStr && jwkStr.startsWith("{")) {
                const jwk = JSON.parse(jwkStr);
                const key = await window.crypto.subtle.importKey(
                    "jwk",
                    jwk,
                    { name: "AES-GCM" },
                    true,
                    ["encrypt", "decrypt"]
                );

                // Save
                localStorage.setItem(`group_key_${keyId}`, jwkStr);
                keyCache.set(keyId, key);
                return key;
            }
        }
    } catch (e) {
        console.error("Network Key Fetch Failed", e);
    }
    return null;
}
